import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown as faTrendingDown,
  faArrowUp as faTrendingUp,
  faCalendar,
  faChartBar,
  faCut,
  faDownload,
  faEuroSign,
  faEye,
  faFilter,
  faMinus,
  faSearch,
  faStar,
} from '@fortawesome/free-solid-svg-icons';

const ServiceAnalysisPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [filterCategory, setFilterCategory] = useState('all');

  const breadcrumb = [{ label: 'Reporty' }, { label: 'Analýza služeb' }];

  const serviceData = [
    {
      id: '1',
      name: 'Střih a foukaná',
      category: 'hair',
      totalBookings: 156,
      totalRevenue: 124800,
      averagePrice: 800,
      averageDuration: 60,
      popularity: 45.6,
      trend: 'up',
      trendValue: 12.5,
      customerSatisfaction: 4.8,
      repeatRate: 78,
    },
    {
      id: '2',
      name: 'Barvení vlasů',
      category: 'coloring',
      totalBookings: 89,
      totalRevenue: 133500,
      averagePrice: 1500,
      averageDuration: 120,
      popularity: 26.0,
      trend: 'up',
      trendValue: 8.3,
      customerSatisfaction: 4.7,
      repeatRate: 65,
    },
    {
      id: '3',
      name: 'Melíry',
      category: 'coloring',
      totalBookings: 67,
      totalRevenue: 80400,
      averagePrice: 1200,
      averageDuration: 90,
      popularity: 19.6,
      trend: 'stable',
      trendValue: 0,
      customerSatisfaction: 4.6,
      repeatRate: 72,
    },
    {
      id: '4',
      name: 'Kosmetické ošetření',
      category: 'cosmetics',
      totalBookings: 45,
      totalRevenue: 54000,
      averagePrice: 1200,
      averageDuration: 90,
      popularity: 13.2,
      trend: 'up',
      trendValue: 15.2,
      customerSatisfaction: 4.9,
      repeatRate: 85,
    },
    {
      id: '5',
      name: 'Manikúra',
      category: 'nails',
      totalBookings: 38,
      totalRevenue: 22800,
      averagePrice: 600,
      averageDuration: 60,
      popularity: 11.1,
      trend: 'down',
      trendValue: -5.8,
      customerSatisfaction: 4.5,
      repeatRate: 68,
    },
    {
      id: '6',
      name: 'Holení',
      category: 'beard',
      totalBookings: 32,
      totalRevenue: 12800,
      averagePrice: 400,
      averageDuration: 30,
      popularity: 9.4,
      trend: 'up',
      trendValue: 3.2,
      customerSatisfaction: 4.4,
      repeatRate: 55,
    },
  ];

  const categories = [
    { id: 'hair', name: 'Úprava vlasů', color: 'bg-blue-100 text-blue-800' },
    { id: 'coloring', name: 'Barvení', color: 'bg-purple-100 text-purple-800' },
    { id: 'cosmetics', name: 'Kosmetika', color: 'bg-pink-100 text-pink-800' },
    { id: 'nails', name: 'Nehty', color: 'bg-red-100 text-red-800' },
    { id: 'beard', name: 'Úprava vousů', color: 'bg-green-100 text-green-800' },
  ];

  const filteredServices = serviceData.filter(service => filterCategory === 'all' || service.category === filterCategory);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return faTrendingUp;
      case 'down':
        return faTrendingDown;
      default:
        return faMinus;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getCategoryInfo = (categoryId: string) => {
    return categories.find(c => c.id === categoryId) || { name: 'Neznámá', color: 'bg-gray-100 text-gray-800' };
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FontAwesomeIcon key={i} icon={faStar} className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  const totalRevenue = serviceData.reduce((sum, service) => sum + service.totalRevenue, 0);
  const totalBookings = serviceData.reduce((sum, service) => sum + service.totalBookings, 0);
  const averageServicePrice = Math.round(totalRevenue / totalBookings);

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Analýza služeb" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Analýza služeb</h1>
                <p className="text-gray-600 mt-1">Detailní analýza výkonnosti jednotlivých služeb</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="week">Tento týden</option>
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Summary Stats */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faCut} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem služeb</p>
                  <p className="text-2xl font-bold text-gray-900">{serviceData.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkové tržby</p>
                  <p className="text-2xl font-bold text-gray-900">{totalRevenue.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faCalendar} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem rezervací</p>
                  <p className="text-2xl font-bold text-gray-900">{totalBookings}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <FontAwesomeIcon icon={faChartBar} className="text-xl text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrná cena</p>
                  <p className="text-2xl font-bold text-gray-900">{averageServicePrice} Kč</p>
                </div>
              </div>
            </div>
          </section>

          {/* Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat služby..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="all">Všechny kategorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          {/* Services Analysis Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služba</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategorie</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rezervace</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tržby</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Populárnost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trend</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spokojenost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredServices.map(service => {
                    const categoryInfo = getCategoryInfo(service.category);
                    return (
                      <tr key={service.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-sm text-gray-500">
                              {service.averagePrice} Kč • {service.averageDuration} min
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${categoryInfo.color}`}>{categoryInfo.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{service.totalBookings}</div>
                          <div className="text-sm text-gray-500">rezervací</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{service.totalRevenue.toLocaleString()} Kč</div>
                          <div className="text-sm text-gray-500">{Math.round((service.totalRevenue / totalRevenue) * 100)}% z celku</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${service.popularity}%` }}></div>
                            </div>
                            <span className="text-sm text-gray-900">{service.popularity}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={getTrendIcon(service.trend)} className={`w-4 h-4 mr-1 ${getTrendColor(service.trend)}`} />
                            <span className={`text-sm ${getTrendColor(service.trend)}`}>
                              {service.trendValue > 0 ? '+' : ''}
                              {service.trendValue}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="flex">{renderStars(service.customerSatisfaction)}</div>
                            <span className="text-sm text-gray-900">{service.customerSatisfaction}</span>
                          </div>
                          <div className="text-xs text-gray-500">{service.repeatRate}% opakování</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 p-1" title="Detail analýzy">
                            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Top Performing Services */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Most Popular Services */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Nejpopulárnější služby</h3>
              <div className="space-y-4">
                {serviceData
                  .sort((a, b) => b.popularity - a.popularity)
                  .slice(0, 5)
                  .map((service, index) => (
                    <div key={service.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{service.name}</p>
                          <p className="text-sm text-gray-600">{service.totalBookings} rezervací</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{service.totalRevenue.toLocaleString()} Kč</p>
                        <p className="text-xs text-gray-500">{service.popularity}%</p>
                      </div>
                    </div>
                  ))}
              </div>
            </section>

            {/* Highest Revenue Services */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Nejvýnosnější služby</h3>
              <div className="space-y-4">
                {serviceData
                  .sort((a, b) => b.totalRevenue - a.totalRevenue)
                  .slice(0, 5)
                  .map(service => (
                    <div key={service.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <FontAwesomeIcon icon={faEuroSign} className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{service.name}</p>
                          <p className="text-sm text-gray-600">{service.averagePrice} Kč/služba</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{service.totalRevenue.toLocaleString()} Kč</p>
                        <p className="text-xs text-gray-500">{service.totalBookings} rezervací</p>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          </div>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faChartBar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Vytvořit graf</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export dat</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faEye} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Srovnat služby</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faStar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Analýza spokojenosti</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ServiceAnalysisPage;
